import { z } from "zod";
import { slugString } from "./slug.js";
import { ProfileRole } from "./profile.js";

// FT-1 — the product feature registry (tooling/features/features.yaml).
// Born 2026-08-12 from the premium-tiering/competitor-analysis session:
// re-deriving "what does JL do today" by hand (or with a 5-agent audit)
// every time PE-5 needs an answer is the cost this file removes. `tier`
// is deliberately open ("unclear" default) — PE-5 owns filling it in,
// this schema only enforces the registry stays well-formed.

// Feature ids are area-prefixed ("<area-id>--<feature-slug>") to stay
// globally unique without a registry-wide numbering scheme, so they run
// longer than a typical slug; area ids stay short (bare area name).
const AreaId = slugString(64);
const FeatureId = slugString(120);

export const FeatureStatus = z.enum([
  "shipped",
  "in_design",
  "planned",
  "parked",
]);

export const FeatureTier = z.enum(["free", "premium", "unclear"]);

// FT-2 — where a feature LIVES, not just that it exists. "partial" is the
// value that carries the weight: the walking-session summary on web-rnw
// renders "0 sets · Volume 0 kg" while the set is really there, so the
// mount exists and lies. Without "partial" the registry would answer
// "it's on web" and that debt would never surface.
//
// "n-a" is deliberately narrow: the surface CANNOT host the feature
// (a platform capability — Health Connect is Android-only). "we chose
// not to build it there" is "absent", which is debt and feeds the lift
// pool. Anything else lets a decision hide as a law of physics.
export const FeatureSurfaceStatus = z.enum([
  "shipped",
  "partial",
  "absent",
  "n-a",
]);

// web-legacy is the old apps/web, still deployed and still the only home
// of a few capabilities (the SL CSV importer, the admin catalog CRUD).
// It is here because the strangler needs the answer to "what do we lose
// if we switch it off" — rnw-strangler §8 keeps that list BY HAND today,
// and a hand-kept list is the same thing that let the lift stop. Note it
// is not part of the lift pool: the pool asks about web-rnw only.
export const FeatureSurfaces = z.object({
  mobile: FeatureSurfaceStatus,
  "web-rnw": FeatureSurfaceStatus,
  "web-legacy": FeatureSurfaceStatus,
});

// FT-3 (2026-08-19) — why a feature that OWES web work is nonetheless not
// the nightly lift's work. Three kinds, and none of them is `n-a`:
//
//   - platform-build — the web half has to be WRITTEN, not moved. Mobile's
//     implementation is native (AsyncStorage outbox, notifee), so nothing
//     crosses. nightly-lift-prompt.md §1 already tells the night to skip
//     these ("costruire da zero ... quello non è un lift"); until now it
//     had to rediscover which ones they were, one wasted night at a time.
//   - chapter — the entry describes a TRACK, not a work unit: its own note
//     enumerates sub-features that are already registry entries in their
//     own right. Its surface column double-counts work tracked elsewhere.
//   - not-behind-mobile — web-rnw is not actually behind mobile. Either the
//     residue lives in shared code (packages/ui, packages/i18n), where a
//     fix lands on both mounts in one commit, or it exists on neither mount.
//     ⛔ Deliberately NOT named after `n-a`: that is a surface that CANNOT
//     host a feature; this is a debt reading that turned out to be a false
//     positive.
//
// ⛔ What this is NOT: an escape from PARITY (rnw-strangler.md §3.8). An
// excluded feature stays debt — it stays in `pool`, it still counts for
// `--gate-stable`, and marking it changes ONE thing: the night stops being
// handed work it cannot finish. "The web should not have this" is not a
// kind, and never will be.
//
// ⛔ And it is not a status the nightly agent may write. The bar is the one
// the reclassification itself was held to: file:line, or a command that was
// run. `why` carries that evidence, which is why it has a floor and not
// just a maximum.
export const LiftExclusionKind = z.enum([
  "platform-build",
  "chapter",
  "not-behind-mobile",
]);

export const LiftExclusion = z.object({
  kind: LiftExclusionKind,
  why: z.string().min(20).max(600),
});

export const FeatureEntry = z.object({
  id: FeatureId,
  name: z.string().min(1).max(160),
  status: FeatureStatus,
  tier: FeatureTier,
  // Both required: a new feature cannot be born without declaring where
  // it lives and who it is for. That is the whole point of FT-2 — the
  // lift stopped because nothing generated its work.
  surfaces: FeatureSurfaces,
  // ProfileRole (schemas/profile.ts) is reused rather than redeclared:
  // a second admin/coach/athlete enum is exactly the drift that lets one
  // of the two forget a role the day a fourth one appears.
  roles: z.array(ProfileRole).min(1),
  note: z.string().max(600).optional(),
  // Absent means "the night can take this". Present means it cannot, and
  // says why — see LiftExclusion above.
  liftExclusion: LiftExclusion.optional(),
});

export const FeatureArea = z.object({
  id: AreaId,
  name: z.string().min(1).max(80),
  features: z.array(FeatureEntry).min(1),
  /**
   * How much this area is worth to the nightly lift, 0..10. Optional, and
   * absent means 0 — the pool then falls back to the two signals it can read
   * for itself (a lying `partial` mount before a missing one, athlete-facing
   * before coach/admin).
   *
   * ⛔ This is the ONE product judgement in the pool ordering, and it belongs
   * to Antenore. It lives in the registry as data, not in the ranking code,
   * so changing what the night works on first is an edit to this file rather
   * than a change of algorithm. Added 2026-08-16: until then the pool came
   * out grouped by area with no order at all, and the prompt's "take the
   * first one you can close" turned a flat list into a bias toward whatever
   * was smallest.
   */
  liftPriority: z.number().int().min(0).max(10).optional(),
});

export const FeatureRegistry = z.object({
  version: z.number().int().positive(),
  areas: z.array(FeatureArea).min(1),
});

export type FeatureStatus = z.infer<typeof FeatureStatus>;
export type FeatureTier = z.infer<typeof FeatureTier>;
export type FeatureSurfaceStatus = z.infer<typeof FeatureSurfaceStatus>;
export type FeatureSurfaces = z.infer<typeof FeatureSurfaces>;
export type LiftExclusionKind = z.infer<typeof LiftExclusionKind>;
export type LiftExclusion = z.infer<typeof LiftExclusion>;
export type FeatureEntry = z.infer<typeof FeatureEntry>;
export type FeatureArea = z.infer<typeof FeatureArea>;
export type FeatureRegistry = z.infer<typeof FeatureRegistry>;
